import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate as useRouterNavigate, useMatch } from 'react-router-dom';
import { useCustomerAuth } from './CustomerAuthContext';

type PageId = 'home' | 'order' | 'passport' | 'history' | 'loginclient' | 'gift' | 'friendprofile' | 'settings' | 'feed' | 'restaurant' | 'venue' | 'friends';

interface PwaNav {
  navigate: (path: string, options?: { replace?: boolean }) => void;
  showOrder: boolean;
  showPassport: boolean;
  showHistory: boolean;
  showLogin: boolean;
  showGift: boolean;
  showFriendProfile: boolean;
  showSettings: boolean;
  showFeed: boolean;
  showRestaurant: boolean;
  showVenue: boolean;
  showFriends: boolean;
  navData: Record<string, string>;
  /** The initial QR code from URL when PWA launched (for OrderStatus useParams fallback) */
  initialQrCode: string | null;
}

const PwaNavContext = createContext<PwaNav | null>(null);

const isStandalone =
  (window.navigator as { standalone?: boolean }).standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

function pathToPage(path: string): PageId {
  if (path.startsWith('/order/')) return 'order';
  if (path.startsWith('/passport')) return 'passport';
  if (path.startsWith('/history')) return 'history';
  if (path.startsWith('/loginclient')) return 'loginclient';
  if (path.startsWith('/gift')) return 'gift';
  if (path.startsWith('/friend-profile')) return 'friendprofile';
  if (path.startsWith('/friends')) return 'friends';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/feed')) return 'feed';
  if (path.startsWith('/r/')) return 'restaurant';
  if (path.startsWith('/v/')) return 'venue';
  return 'home';
}

export function PwaNavProvider({ children }: { children: React.ReactNode }) {
  const routerNavigate = useRouterNavigate();
  const { isAuthenticated } = useCustomerAuth();
  const isAuthRef = useRef(isAuthenticated);
  isAuthRef.current = isAuthenticated;

  // Detect initial QR code from URL
  const orderMatchShort = useMatch('/order/:qrCode');
  const orderMatchLong = useMatch('/order/:qrCode/ct/:ct');
  const orderMatch = orderMatchShort ?? orderMatchLong;
  const passportMatch = useMatch('/passport');
  const historyMatch = useMatch('/history');
  const loginMatch = useMatch('/loginclient');
  const giftMatch = useMatch('/gift');
  const friendProfileMatch = useMatch('/friend-profile');
  const settingsMatch = useMatch('/settings');
  const feedMatch = useMatch('/feed');
  const restaurantMatch = useMatch('/r/:restaurantSlug');
  const venueMatch = useMatch('/v/:venueSlug');
  const friendsMatch = useMatch('/friends');

  // Initial page based on URL at mount
  // If the QR code points to a finished order, skip straight to home
  // For cold PWA start with no order in URL, restore from localStorage
  const initialPage = useMemo<PageId>(() => {
    if (orderMatch) {
      const qr = orderMatch.params.qrCode;
      if (qr && localStorage.getItem(`olso_order_done_${qr}`)) {
        return 'home';
      }
      return 'order';
    }
    if (passportMatch) return 'passport';
    if (historyMatch) return 'history';
    if (loginMatch) return 'loginclient';
    // Cold PWA start with no specific route — check for saved active order
    if (isStandalone) {
      const savedQr = localStorage.getItem('olso_active_order');
      if (savedQr && !localStorage.getItem(`olso_order_done_${savedQr}`)) {
        return 'order';
      }
    }
    return 'home';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialQrCode = useMemo(() => {
    if (orderMatch?.params.qrCode) {
      const qr = orderMatch.params.qrCode;
      if (localStorage.getItem(`olso_order_done_${qr}`)) return null;
      return qr;
    }
    // Restore from localStorage for cold PWA start
    if (isStandalone) {
      const savedQr = localStorage.getItem('olso_active_order');
      if (savedQr && !localStorage.getItem(`olso_order_done_${savedQr}`)) {
        return savedQr;
      }
    }
    return null;
  }, []);

  // State-based page tracking for standalone mode
  const [activePage, setActivePage] = useState<PageId>(initialPage);
  const initialNavData = useMemo((): Record<string, string> => {
    // If restoring an order from localStorage, seed navData with qrCode
    if (initialPage === 'order' && initialQrCode) {
      return { qrCode: initialQrCode };
    }
    return {} as Record<string, string>;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [navData, setNavData] = useState<Record<string, string>>(initialNavData);

  // Track current history state so back button can restore it (no-op back)
  const currentStateRef = useRef<{ page: PageId; path: string; data: Record<string, string> }>({
    page: initialPage, path: window.location.pathname, data: initialNavData,
  });

  const navigate = useCallback((path: string, { replace = false }: { replace?: boolean } = {}) => {
    const qIdx = path.indexOf('?');
    const basePath = qIdx !== -1 ? path.slice(0, qIdx) : path;
    const data: Record<string, string> = {};
    if (qIdx !== -1) {
      const params = new URLSearchParams(path.slice(qIdx));
      params.forEach((v, k) => { data[k] = v; });
    }
    // Extract qrCode from path so OrderStatus can read it in PWA standalone mode
    const orderMatch = basePath.match(/^\/order\/([^/]+)/);
    if (orderMatch) data.qrCode = orderMatch[1];
    setNavData(data);
    if (isStandalone) {
      const page = pathToPage(path);
      // Pages not in the PWA overlay enum (e.g. /admin) fall back to
      // real router navigation. /feed and /r/:slug stay in standalone overlay.
      if (page === 'home' && basePath !== '/' && basePath !== '/home') {
        routerNavigate(path);
        return;
      }
      // Extract restaurantSlug from /r/:slug so the overlay can render the right menu
      const restaurantMatch = basePath.match(/^\/r\/([^/]+)/);
      if (restaurantMatch) data.restaurantSlug = restaurantMatch[1];
      const venuePathMatch = basePath.match(/^\/v\/([^/]+)/);
      if (venuePathMatch) data.venueSlug = venuePathMatch[1];
      setActivePage(page);
      // Push state WITHOUT changing URL — keeps iOS standalone mode intact
      const historyState = { page, path, data };
      currentStateRef.current = historyState;
      if (replace) {
        history.replaceState(historyState, '');
      } else {
        history.pushState(historyState, '');
      }
    } else {
      // In browser: use normal router navigation
      routerNavigate(path);
    }
  }, [routerNavigate]);

  const value = useMemo<PwaNav>(() => {
    if (isStandalone) {
      return {
        navigate,
        showOrder: activePage === 'order',
        showPassport: activePage === 'passport',
        showHistory: activePage === 'history',
        showLogin: activePage === 'loginclient',
        showGift: activePage === 'gift',
        showFriendProfile: activePage === 'friendprofile',
        showSettings: activePage === 'settings',
        showFeed: activePage === 'feed',
        showRestaurant: activePage === 'restaurant',
        showVenue: activePage === 'venue',
        showFriends: activePage === 'friends',
        navData,
        initialQrCode,
      };
    }
    // Browser mode: derive from URL matches
    return {
      navigate,
      showOrder: !!orderMatch,
      showPassport: !!passportMatch,
      showHistory: !!historyMatch,
      showLogin: !!loginMatch,
      showGift: !!giftMatch,
      showFriendProfile: !!friendProfileMatch,
      showSettings: !!settingsMatch,
      showFeed: !!feedMatch,
      showRestaurant: !!restaurantMatch,
      showVenue: !!venueMatch,
      showFriends: !!friendsMatch,
      navData,
      initialQrCode: null,
    };
  }, [isStandalone ? activePage : null, navigate, orderMatch, passportMatch, historyMatch, loginMatch, giftMatch, friendProfileMatch, settingsMatch, feedMatch, restaurantMatch, venueMatch, friendsMatch, navData, initialQrCode]);

  // Handle browser back/forward buttons in PWA standalone mode
  useEffect(() => {
    if (!isStandalone) return;
    const initState = currentStateRef.current;
    history.replaceState(initState, '');
    history.pushState(initState, '');

    const handler = (e: PopStateEvent) => {
      if (!isAuthRef.current) {
        // Not authenticated → back is a no-op, stay on current page
        const cur = currentStateRef.current;
        setActivePage(cur.page);
        setNavData(cur.data);
        history.pushState(cur, '');
      } else if (e.state?.page) {
        // Authenticated → allow normal back navigation
        setActivePage(e.state.page);
        setNavData(e.state.data || {});
        history.pushState(e.state, '');
      } else {
        setActivePage(initialPage);
        setNavData({});
        history.pushState(initState, '');
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for service worker messages (notification clicks)
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    // On startup, ask SW if there's a pending notification URL (iOS PWA re-launch)
    navigator.serviceWorker?.ready.then((reg) => {
      reg.active?.postMessage({ type: 'GET_PENDING_NOTIF' });
    });
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [navigate]);

  return (
    <PwaNavContext.Provider value={value}>
      {children}
    </PwaNavContext.Provider>
  );
}

export function usePwaNav(): PwaNav {
  const ctx = useContext(PwaNavContext);
  if (!ctx) throw new Error('usePwaNav must be inside PwaNavProvider');
  return ctx;
}

/** Drop-in replacement for useNavigate that respects PWA mode */
export function useAppNavigate() {
  const { navigate } = usePwaNav();
  return navigate;
}
